import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Sparkles, Send, Calendar, Users, Cake, MessageSquare,
  Check, ArrowRight, Phone, Mail, Clock,
} from "lucide-react";
import { orderFormOptions } from "../data/mock";
import { toast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";

import PersonalInfoStep from "../components/order/PersonalInfoStep";
import CakeDetailsStep from "../components/order/CakeDetailsStep";
import EventStep from "../components/order/EventStep";
import DescriptionStep from "../components/order/DescriptionStep";

const STEPS = [
  { id: 1, label: "Vos infos", icon: Users },
  { id: 2, label: "Le gâteau", icon: Cake },
  { id: 3, label: "Événement", icon: Calendar },
  { id: 4, label: "Détails", icon: MessageSquare },
];

const INITIAL_FORM_DATA = {
  name: "", email: "", phone: "",
  cakeType: "", flavor: "", servings: "",
  eventDate: "", eventType: "",
  description: "", budget: "",
  acceptConditions: false,
};

function getCanProceed(step, formData) {
  if (step === 1) return formData.name && formData.email && formData.phone;
  if (step === 2) return formData.cakeType;
  if (step === 3) return formData.eventDate;
  if (step === 4) return formData.acceptConditions;
  return false;
}

function getStepClassName(stepId, currentStep) {
  if (stepId === currentStep) return "bg-[#E5007D] text-white shadow-md";
  if (stepId < currentStep) return "bg-white text-green-600 cursor-pointer";
  return "bg-white/50 text-gray-400";
}

// Fonction utilitaire pour encoder les données pour Netlify
const encode = (data) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

export default function CommandePage() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateFormData = (fields) => {
    setFormData((prev) => ({ ...prev, ...fields }));
  };

  const handleNext = () => {
    if (getCanProceed(step, formData)) {
      setStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !formData.acceptConditions) return;

    setIsSubmitting(true);

    try {
      // Envoi direct à Netlify Forms
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "demande-devis", ...formData }),
      });
      if (!response.ok) throw new Error("La demande n'a pas pu être envoyée.");

      toast({
        title: "Demande envoyée ! 🎉",
        description: "Votre demande de devis a bien été reçue. Je reviens vers vous très vite.",
      });

      setFormData(INITIAL_FORM_DATA);
      setStep(1);
    } catch (err) {
      console.error("Netlify Submission Error:", err);
      toast({
        variant: "destructive",
        title: "Oups !",
        description: "Une erreur est survenue lors de l'envoi. Veuillez réessayer.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#F6F7F1]">
      <Toaster />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-playfair text-4xl font-bold text-gray-900 mb-2">
            Demande de Devis Personnalisé
          </h1>
          <p className="text-gray-500 max-w-xl mx-auto">
            Créez le gâteau de vos rêves en quelques étapes simples.
          </p>
        </div>

        {/* Stepper */}
        <div className="max-w-3xl mx-auto mb-12 grid grid-cols-4 gap-2">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.id}
                onClick={() => s.id < step && setStep(s.id)}
                className={`flex flex-col items-center p-3 rounded-xl transition-all ${getStepClassName(
                  s.id,
                  step
                )}`}
              >
                <Icon className="w-5 h-5 mb-1" />
                <span className="text-xs font-medium hidden sm:inline">{s.label}</span>
              </div>
            );
          })}
        </div>

        {/* Form Container */}
        <div className="max-w-3xl mx-auto bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <form
            name="demande-devis"
            method="POST"
            data-netlify="true"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Input secret requis pour que Netlify intercepte les requêtes en AJAX */}
            <input type="hidden" name="form-name" value="demande-devis" />

            {/* Rendu dynamique des étapes */}
            {step === 1 && (
              <PersonalInfoStep formData={formData} updateFields={updateFormData} />
            )}
            {step === 2 && (
              <CakeDetailsStep formData={formData} updateFields={updateFormData} options={orderFormOptions} />
            )}
            {step === 3 && (
              <EventStep formData={formData} updateFields={updateFormData} />
            )}
            {step === 4 && (
              <DescriptionStep formData={formData} updateFields={updateFormData} />
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-100">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors"
                >
                  Retour
                </button>
              ) : (
                <div />
              )}

              {step < 4 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!getCanProceed(step, formData)}
                  className="px-6 py-2.5 rounded-xl bg-[#E5007D] text-white hover:bg-[#c4006b] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center gap-2"
                >
                  Continuer <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting || !getCanProceed(step, formData)}
                  className="px-6 py-2.5 rounded-xl bg-[#E5007D] text-white hover:bg-[#c4006b] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center gap-2"
                >
                  {isSubmitting ? "Envoi..." : "Envoyer ma demande"} <Send className="w-4 h-4" />
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
